
// tutor-frontend/src/modules/center-services/AccessibleService.ts

import { getUserSession } from "common/session/user-session";
import { apiClient } from "infra/api-client";
import { BASE_URL } from "global";
import moment, { Moment } from "moment";
import pDefer, { DeferredPromise } from "p-defer";

interface CenterServiceDto {
  effective_date: string
  end_date: string;
  service_type: string
}

interface CenterService {
  effective_date: Moment
  end_date: Moment;
  service_type: string
}

const ServiceType = {
  GAMES: 'games',
  ENRICHMENT: 'enrichment',
  ONLINE_SESSION: 'video_conferencing'
} as const;

const BUNDLE_TOKEN = 'games_enrichment';

// Map any legacy labels to a canonical key

const NORMALIZE: Record<string, keyof typeof ServiceType> = {
  games: "GAMES",
  enrichment: "ENRICHMENT",
  lll_educational: "ENRICHMENT",
  lll: "ENRICHMENT",
  video_conferencing: "ONLINE_SESSION",
};

function toCanonicalKey(s: string | undefined | null): keyof typeof ServiceType | undefined {
  if (!s) return undefined;
  const k = s.toString().trim().toLowerCase();
  return NORMALIZE[k];
}


export class AccessibleService {

  private static cache_expiry = 10 * 60 * 1000;
  private centerServices?: CenterService[];
  private defer: DeferredPromise<void> | null = null;

  async init() {

    if(!this.defer) {
      this.defer = pDefer<void>();
      this.fetchData().then();
    }

    return this.defer.promise;
  }

  private async fetchData() {
    let resp: any;
    try {
      const { centerKey } = getUserSession();
      if (!centerKey) {
        this.centerServices = [];
        return;
      }

      resp = await apiClient.post(
        BASE_URL + "centerServices/get-accessible-services-by-center",
        { fk_cent_key: centerKey }
      );


      const raw = resp?.data ?? resp; // some clients already unwrap .data
      const list: CenterServiceDto[] =
        Array.isArray(raw)         ? raw :
          Array.isArray(raw?.data)   ? raw.data :
            Array.isArray(raw?.rows)   ? raw.rows :
              [];

      this.centerServices = list.map(e => ({
        ...e,
        effective_date: moment(e.effective_date),
        end_date: moment(e.end_date),
      }));
    } catch (err) {
      console.error("AccessibleService.fetchData failed:", err, resp);
      this.centerServices = [];
    } finally {
      this.defer?.resolve?.();
      setTimeout(() => { this.defer = null; }, AccessibleService.cache_expiry);
    }
  }


  hasAccessToGames() {
    return this.hasAccessToService(ServiceType.GAMES);
  }

  hasAccessToLll() {
    return this.hasAccessToService(ServiceType.ENRICHMENT);
  }

  hasAccessToEnrichment() {
    return this.hasAccessToService(ServiceType.ENRICHMENT);
  }

  hasAccessToOnlineSession() {
    return this.hasAccessToService(ServiceType.ONLINE_SESSION);
  }


  private hasAccessToService(serviceCanonical: (typeof ServiceType)[keyof typeof ServiceType]) {
    const now = moment();

    const match = this.centerServices?.find((row) => {
      const rawType = row.service_type?.toString().trim().toLowerCase();
      const isBundle = rawType === BUNDLE_TOKEN;
      const rowKey = toCanonicalKey(row.service_type);

      if (isBundle) {
        const bundleCovers =
          serviceCanonical === ServiceType.GAMES ||
          serviceCanonical === ServiceType.ENRICHMENT;
        if (!bundleCovers) return false;
      } else {
      if (!rowKey) return false;

      if (ServiceType[rowKey] !== serviceCanonical) return false;
      }

      return (
        row.effective_date.isSameOrBefore(now, "day") &&
        row.end_date.isSameOrAfter(now, "day")
      );
    });

    return !!match;
  }
}

export const accessibleService = new AccessibleService();